import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, MessageSquare, Plus, Trash2, MessageCircle, CalendarClock } from 'lucide-react';
import { preload } from 'swr';
import axios from 'axios';
import API_BASE from './api';
import Dashboard from './pages/Dashboard';
import Chat from './pages/Chat';
import Coaches from './pages/Coaches';
import Login from './pages/Login';
import Lookups from './pages/Lookups';

const AUTH_STORAGE_KEY = 'letterly_admin_auth';

const dashboardFetcher = async (url) => {
  const res = await fetch(url);
  const data = await res.json();
  return data.requests || [];
};

function formatRelativeTime(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const mins = Math.floor((now - d) / 60000);
  const hrs = Math.floor((now - d) / 3600000);
  const days = Math.floor((now - d) / 86400000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  if (days === 1) return 'Yesterday';
  if (days < 7) return d.toLocaleDateString('en-US', { weekday: 'short' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      return !!localStorage.getItem(AUTH_STORAGE_KEY);
    } catch {
      return false;
    }
  });
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/chat/sessions`);
      setSessions(res.data.sessions || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchSessions();
  }, [isAuthenticated, fetchSessions]);

  useEffect(() => {
    // Keep auth state in sync across tabs/windows
    const onStorage = (e) => {
      if (e.key !== AUTH_STORAGE_KEY) return;
      setIsAuthenticated(!!e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Light polling so the left "Chat History" updates without hard refresh
  useEffect(() => {
    if (!isAuthenticated) return;
    const t = setInterval(() => {
      fetchSessions();
    }, 15000);
    return () => clearInterval(t);
  }, [isAuthenticated, fetchSessions]);

  const handleLogin = () => setIsAuthenticated(true);

  const handleLogout = () => {
    try {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch { /* ignore */ }
    setIsAuthenticated(false);
    setActiveSessionId(null);
    setSessions([]);
    navigate('/login', { replace: true });
  };

  const handleSelectSession = useCallback((id) => {
    setActiveSessionId(id);
    if (location.pathname !== '/chat') navigate('/chat');
  }, [location.pathname, navigate]);

  const handleNewChat = useCallback(() => {
    setActiveSessionId(null);
    if (location.pathname !== '/chat') navigate('/chat');
  }, [location.pathname, navigate]);

  const handleDeleteSession = useCallback(async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await axios.delete(`${API_BASE}/api/chat/sessions/${id}`);
      setSessions(prev => prev.filter(s => s.id !== id));
      if (id === activeSessionId) setActiveSessionId(null);
    } catch { /* silent */ }
  }, [activeSessionId]);

  return (
    <div className="app-container">
      {isAuthenticated && (
        <aside className="sidebar">
          <div className="sidebar-logo">
            <img src="/logo-letterly.png" alt="Letterly" style={{ height: 36 }} />
          </div>
          <nav className="sidebar-nav">
            <NavLink
              to="/dashboard"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onMouseEnter={() => preload(`${API_BASE}/api/dashboard`, dashboardFetcher)}
            >
              <LayoutDashboard size={20} />
              Dashboard
            </NavLink>
            <NavLink
              to="/coaches"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
            >
              <Users size={20} />
              Coaches
            </NavLink>
            <NavLink
              to="/lookups"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
            >
              <CalendarClock size={20} />
              Courses & Terms
            </NavLink>
            <NavLink
              to="/chat"
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setActiveSessionId(null)}
            >
              <MessageSquare size={20} />
              AI Chat
            </NavLink>
          </nav>

          {location.pathname === '/chat' && (
            <div className="chat-history-section">
              <div className="chat-history-header">
                <span className="chat-history-title">Chat History</span>
                <button className="chat-new-btn" onClick={handleNewChat}>
                  <Plus size={12} /> New
                </button>
              </div>
              <div className="chat-history-list">
                {sessions.length === 0 ? (
                  <div style={{ padding: '1rem 0.5rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.72rem', lineHeight: 1.5 }}>
                    <MessageCircle size={22} color="#d1d5db" style={{ margin: '0 auto 4px' }} />
                    <div>No chats yet</div>
                  </div>
                ) : (
                  sessions.map(s => (
                    <div
                      key={s.id}
                      className={`chat-session-item ${s.id === activeSessionId ? 'active' : ''}`}
                      onClick={() => handleSelectSession(s.id)}
                    >
                      <MessageCircle size={13} color={s.id === activeSessionId ? '#c1009d' : '#9ca3af'} style={{ flexShrink: 0, marginTop: 1 }} />
                      <div className="chat-session-info">
                        <div className="chat-session-title">{s.title || 'New Chat'}</div>
                        <div className="chat-session-time">{formatRelativeTime(s.updated_at)}</div>
                      </div>
                      <button
                        className="chat-session-delete"
                        title="Delete chat"
                        onClick={(e) => handleDeleteSession(s.id, e)}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          <div style={{ marginTop: 'auto', padding: '0.75rem 0.25rem 0.25rem' }}>
            <button
              type="button"
              onClick={handleLogout}
              className="btn btn-secondary"
              style={{ width: '100%' }}
            >
              Logout
            </button>
          </div>
        </aside>
      )}
      <main className="main-content">
        <Routes>
          <Route
            path="/"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
            }
          />
          <Route
            path="/login"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login onLogin={handleLogin} />
            }
          />
          <Route
            path="/dashboard"
            element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/coaches"
            element={isAuthenticated ? <Coaches /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/lookups"
            element={isAuthenticated ? <Lookups /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/chat"
            element={
              isAuthenticated ? (
                <Chat
                  activeSessionId={activeSessionId}
                  setActiveSessionId={setActiveSessionId}
                  refreshSessions={fetchSessions}
                />
              ) : <Navigate to="/login" replace />
            }
          />
        </Routes>
      </main>
    </div>
  );
}

export default App;
